import { useEffect, useState } from 'react';

const MESSAGES = ['The harbor approaches…', 'Ropes are tied…', 'The town awaits…'];

interface TransitionStageProps {
  onComplete: () => void;
  /** Total time (ms) to spend on this stage before invoking onComplete. */
  totalMs?: number;
}

export function TransitionStage({ onComplete, totalMs = 2400 }: TransitionStageProps): JSX.Element {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const step = totalMs / MESSAGES.length;
    const ticks = MESSAGES.map((_, i) =>
      window.setTimeout(() => setIndex(i), Math.floor(step * i)),
    );
    const finish = window.setTimeout(onComplete, totalMs);
    return () => {
      ticks.forEach((t) => window.clearTimeout(t));
      window.clearTimeout(finish);
    };
  }, [onComplete, totalMs]);

  return (
    <main
      className="relative min-h-dvh flex items-center justify-center px-4 overflow-hidden"
      role="status"
      aria-live="polite"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(20, 50, 71, 0.0) 0%, rgba(12, 34, 51, 0.7) 80%)',
        }}
      />
      <div className="relative text-center">
        <div className="mb-6 flex items-center justify-center gap-3 text-candle/60">
          <span aria-hidden className="h-px w-10 bg-candle/40" />
          <span aria-hidden className="h-2 w-2 rounded-full bg-candle animate-pulse" />
          <span aria-hidden className="h-px w-10 bg-candle/40" />
        </div>
        <p
          key={index}
          className="font-display text-xl sm:text-2xl text-parchment tracking-wider stage-fade-in-slow"
        >
          {MESSAGES[index]}
        </p>
      </div>
    </main>
  );
}
